import { MouseEvent, useContext, useMemo, useState } from "react";
import {
  Box,
  Card,
  Divider,
  Grid2,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import {
  DashboardGainersAndLosers,
  DashboardHeader,
  DashboardPageNews,
} from "@features/dashboard/components";
import { AssetsChart, AssetsChartLegend } from "@components";
import { GainLossTypography } from "@rentablo/components";
import { LoadingPlaceHolder } from "@rentablo/components";
import { toCurrency } from "@helpers";
import { useGetAccountsCategorize } from "@api";
import { prepareAssetsChartData } from "@features/dashboard/utils";
import { AppLayoutContext } from "@providers/AppLayoutProvider.tsx";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";
import { InvestmentsChartDataStatistic } from "../../../../hooks/chart/useInvestmentsChartDataTotal";

const DashboardWrapper = styled(Card)`
  flex-grow: 1;
  padding: ${({ theme }) => theme.spacing(2.5, 4, 5)};
  height: fit-content;
  display: flex;
  flex-direction: column;
  min-height: 635px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    min-height: unset;
  }
`;

const MainChartContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChartWrapper = styled(Grid2)`
  display: flex;
  justify-content: space-between;
  align-items: self-start;
  height: 100%;

  @media (max-width: 1350px) {
    gap: 0;
  }
`;

const CustomDivider = styled(Divider)`
  margin: 0 25px 0 25px;
  height: 415px;
  border-color: ${({ theme }) => theme.palette.border.secondary};
`;

const NewsSection = styled.div`
  display: flex;
  align-items: self-start;
  flex: 1;
  overflow: hidden;
`;

type DashboardViews = "chart" | "gainersLosers";

interface Props {
  totalValue?: number;
  statistic?: InvestmentsChartDataStatistic;
  currency: string;
  isStatisticsLoading?: boolean;
  showNews?: boolean;
  fullWidth?: boolean;
  statisticsFontVariant?: "subtitle2" | "body2";
}

export const DashboardChartContainer = ({
  totalValue = 0,
  statistic,
  currency,
  isStatisticsLoading,
  statisticsFontVariant: statisticsFontSize = "subtitle2",
  showNews,
  fullWidth,
}: Props) => {
  const [dashboardView, setDashboardView] = useState<DashboardViews>("chart");
  const theme = useTheme();
  const { t } = useTranslation();
  const { sidebarCollapsed } = useContext(AppLayoutContext);
  const { accountIds, isFetching: areAccountsLoading } =
    useDashboardSelectedGroupsContext();

  const isLess1100 = useMediaQuery("(max-width: 1100px)");
  const isLess1300 = useMediaQuery("(max-width: 1300px)");
  const collapseHeader = (!sidebarCollapsed && isLess1300) || isLess1100;

  const { data: categories, isFetching: isChartDataLoading } =
    useGetAccountsCategorize(
      { id: accountIds || [] },
      { enabled: !areAccountsLoading }
    );
  const chartData = useMemo(
    () => prepareAssetsChartData(categories),
    [categories]
  );

  const handleViewChange = (_: MouseEvent<HTMLElement>, value: string) => {
    if (!value || value === dashboardView) return;
    setDashboardView(value as DashboardViews);
  };

  return (
    <DashboardWrapper>
      <DashboardHeader
        totalValue={totalValue}
        currency={currency}
        changeToday={statistic?.changeToday ?? 0}
        collapseHeader={collapseHeader}
        view={dashboardView}
        handleViewChange={handleViewChange}
        fullWidth={fullWidth}
      />
      <MainChartContainer>
        <ChartWrapper>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="self-start"
            flexDirection="column"
            width={showNews ? "40%" : "100%"}
            minWidth="320px"
            height="100%"
            flexShrink={0}
          >
            <Box
              display={dashboardView === "chart" ? "block" : "none"}
              width="100%"
            >
              <AssetsChartLegend />
              <AssetsChart
                data={chartData}
                isFetching={areAccountsLoading || isChartDataLoading}
              />
              {!isStatisticsLoading && !areAccountsLoading ? (
                <Box
                  mt={2}
                  sx={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "14px",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body4"
                      color={theme.palette.text.secondary}
                    >
                      {t(
                        "dashboardPage.currentAssetsBlock.statistic.returnOnInvestment"
                      )}
                    </Typography>
                    <GainLossTypography
                      variant={statisticsFontSize}
                      value={statistic?.valuationGain ?? 0}
                    >
                      {toCurrency(statistic?.valuationGain, currency)}
                    </GainLossTypography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body4"
                      color={theme.palette.text.secondary}
                    >
                      {t("dashboardPage.currentAssetsBlock.statistic.cash")}
                    </Typography>
                    <GainLossTypography
                      variant={statisticsFontSize}
                      value={statistic?.cashBalance ?? 0}
                    >
                      {toCurrency(statistic?.cashBalance, currency)}
                    </GainLossTypography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body4"
                      color={theme.palette.text.secondary}
                    >
                      {t(
                        "dashboardPage.currentAssetsBlock.statistic.dividends"
                      )}
                    </Typography>
                    <GainLossTypography
                      variant={statisticsFontSize}
                      value={statistic?.dividendGain ?? 0}
                    >
                      {toCurrency(statistic?.dividendGain, currency)}
                    </GainLossTypography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body4"
                      color={theme.palette.text.secondary}
                    >
                      {t(
                        "dashboardPage.currentAssetsBlock.statistic.realizedGain"
                      )}
                    </Typography>
                    <GainLossTypography
                      variant={statisticsFontSize}
                      value={statistic?.realizedGain ?? 0}
                    >
                      {toCurrency(statistic?.realizedGain, currency)}
                    </GainLossTypography>
                  </Box>
                </Box>
              ) : (
                <Box width="100%" pr={showNews ? "16px" : 0}>
                  <LoadingPlaceHolder height="114px" />
                </Box>
              )}
            </Box>

            <Box
              display={dashboardView === "gainersLosers" ? "block" : "none"}
              justifyContent="center"
              width="100%"
            >
              <DashboardGainersAndLosers />
            </Box>
          </Box>
          {showNews && (
            <NewsSection>
              <CustomDivider orientation="vertical" />
              <Box width="100%">
                <DashboardPageNews mode="short" />
              </Box>
            </NewsSection>
          )}
        </ChartWrapper>
      </MainChartContainer>
    </DashboardWrapper>
  );
};
